import { api } from '../../../services/api';
import { dateService } from '../../../services/date';
import { checkAllSites } from '../../base/common/utils';

export class PublisherDashboardService {
  constructor() {
    this.Api = api;
  }

  getStatsByDay(pubId, params) {
    checkAllSites(params);
    const endpoint = `/api/v0/publishers/${pubId}/stats_by_day/`;
    return this.Api.get(endpoint, params).then(resp => {
      return resp.data.data[0].stats;
    });
  }

  getStatsByMonth(pubId, params = {}) {
    checkAllSites(params);
    const endpoint = `/api/v0/publishers/${pubId}/stats_by_month/`;
    return this.Api.get(endpoint, params).then(resp => {
      return resp.data.data[0].stats;
    });
  }

  getStatsByYear(pubId, params = {}) {
    checkAllSites(params);
    const endpoint = `/api/v0/publishers/${pubId}/stats_by_year/`;
    return this.Api.get(endpoint, params).then(resp => {
      return resp.data.data[0].stats;
    });
  }

  getMerchantStatsByDay(merchId, params) {
    const endpoint = `/api/v0/merchants/${merchId}/merch_ed/stats_by_day/`;
    checkAllSites(params);
    return this.Api.get(endpoint, params).then(resp => {
      return resp.data.data[0].stats;
    });
  }

  getMerchantStatsByMonth(merchId, params = {}) {
    const endpoint = `/api/v0/merchants/${merchId}/merch_ed/stats_by_month/`;
    checkAllSites(params);
    return this.Api.get(endpoint, params).then(resp => {
      return resp.data.data[0].stats;
    });
  }

  getMerchantStatsByYear(merchId, params = {}) {
    const endpoint = `/api/v0/merchants/${merchId}/merch_ed/stats_by_year/`;
    checkAllSites(params);
    return this.Api.get(endpoint, params).then(resp => {
      return resp.data.data[0].stats;
    });
  }

  getMerchantTopArticles(merchId, params = {}) {
    const endpoint = `/api/v0/merchants/${merchId}/merch_ed/top_articles/`;
    checkAllSites(params);
    return this.Api.get(endpoint, params).then(resp => {
      return resp.data.data[0].stats;
    });
  }

  getMerchantStatsByEdit(merchId, params = {}) {
    const endpoint = `/api/v0/merchants/${merchId}/merch_ed/stats_by_edit/`;
    checkAllSites(params);
    return this.Api.get(endpoint, params).then(resp => {
      return resp.data.data[0].stats;
    });
  }

  getLifetimeEarnings(pubId, params = {}) {
    checkAllSites(params);
    const endpoint = `/api/v0/publishers/${pubId}/earnings_stats/`;
    return this.Api.get(endpoint, params).then(resp => {
      return resp.data.data[0].stats;
    });
  }

  /* Graph helpers */
  generateDatesForRange(startDate, endDate, timeUnit, rangeType) {
    /*
     * Generate all dates for data points between two dates,
     * incremented by timeUnit (day or month)
     */
    const numDatesMap = {
      sevenDay: 7,
      halfYear: 6,
    };
    let numDates = numDatesMap[rangeType];

    if (!numDates) {
      /*
       * The day of month/month of year are the most accurate indicators for these ranges.
       * moment.duration returns a float based on how much of the day/month has elapsed,
       * and there's no consistent way to round it.
       */
      if (rangeType === 'thisYear') {
        numDates = endDate.month() + 1; // Months are 0-indexed
      } else if (rangeType === 'thisMonth' || rangeType === 'lastMonth') {
        numDates = endDate.date();
      } else {
        // Round down for partial year labeling
        numDates = Math.floor(dateService.subtractDates(startDate, endDate, timeUnit));
      }
    }

    const dates = [];
    for (let i = 0; i < numDates; i++) {
      const date = dateService.offsetFromDate(startDate, i, timeUnit);
      // API date format: 2019-10-01
      dates.push(dateService.formatDate(date, 'Y-MM-DD'));
    }

    return dates;
  }

  formatDateLabels(statsWithLabels, timeUnit) {
    /*
     * timeUnit: day|month|year
     * For example, if timeUnit was 'day', dateLabel would be:
     *   '30 Sep', '1 Oct', '2', '3', ...
     */
    const updatedStats = [];

    // Convert month from integer to text (1 => Jan)
    statsWithLabels.forEach((data, i) => {
      const dateFormat = 'D-MMM-Y'; // 1-jan-2019
      const formattedLabel = dateService.formatDate(data.dateLabel, dateFormat);

      data.dateLabel = formattedLabel;
    });

    // Omit month or year if it's the same as the previous label's month/year
    statsWithLabels.forEach((data, i) => {
      const dateParts = data.dateLabel.split('-');
      const previousLabel = i > 0 ? statsWithLabels[i - 1].dateLabel : null;
      const previousParts = previousLabel ? previousLabel.split('-') : null;

      let label;
      let secondLabel;

      if (timeUnit === 'day') {
        if (!previousParts || previousParts[1] !== dateParts[1]) {
          secondLabel = dateParts[1]; // 'jan'
        }
        label = dateParts[0]; // '2'
      } else if (timeUnit === 'month') {
        if (!previousParts || previousParts[2] !== dateParts[2]) {
          secondLabel = dateParts[2]; // '2019'
        }
        label = dateParts[1]; // 'feb'
      } else if (timeUnit === 'year') {
        label = dateParts[2]; // '2019'
      }

      updatedStats.push({
        dateLabel: label,
        secondLabel,
        stats: data.stats,
      });
    });

    return updatedStats;
  }

  /* Helper functions (alphabetized) */
  calculatePercentChange(newStat, oldStat) {
    if (!oldStat) return '-';

    return Math.round(((newStat - oldStat) / oldStat) * 100);
  }

  displayPercentChange(value) {
    // isNaN('-') evaluates to true => Display '-' if there's no stat
    return isNaN(value) ? value : `${Math.abs(value)}%`;
  }

  getRanking(change) {
    if (change < 0) {
      return 'poor';
    }
    if (change >= 0 && change <= 5) {
      return 'fair';
    }
    if (change > 5) {
      return 'good';
    }
    // When there's no previous data to compare
    return 'neutral';
  }
}

export default new PublisherDashboardService();
